import React, { useState, useEffect, useRef } from 'react';
import QRCodeViewer from '../QRCodeViewer';
import apiService from '../../services/api';

type QRView = 'overview' | 'bottles' | 'analytics' | 'roi';

const QR_VIEWS: Array<{ key: QRView; label: string }> = [
  { key: 'overview', label: 'Overview' },
  { key: 'bottles', label: 'Bottles' },
  { key: 'analytics', label: 'Analytics' },
  { key: 'roi', label: 'ROI' },
];

interface QRCode {
  _id: string;
  name: string;
  channel: string;
  code: string;
  url: string;
  qrCodeUrl: string;
  totalClicks: number;
  uniqueVisitors: number;
  conversions: number;
  isActive: boolean;
  description: string;
  createdAt: string;
  bottleType?: string;
  batchNumber?: string;
  productionDate?: string;
  expiryDate?: string;
  campaignId?: string;
  campaignName?: string;
  costPerClick?: number;
  totalSpent?: number;
}

interface QRCodeAnalytics {
  totalCodes: number;
  activeCodes: number;
  totalClicks: number;
  totalConversions: number;
  totalSpent: number;
  overallConversionRate: string;
  channelPerformance: Array<{
    _id: string;
    totalClicks: number;
    totalConversions: number;
    avgConversionRate: number;
  }>;
  bottlePerformance: Array<{
    _id: string;
    totalClicks: number;
    totalConversions: number;
    avgConversionRate: number;
  }>;
  geographicPerformance: Array<{
    _id: string;
    totalClicks: number;
    totalConversions: number;
  }>;
  devicePerformance: Array<{
    _id: string;
    totalClicks: number;
    totalConversions: number;
  }>;
  recentActivity: Array<{
    code: string;
    channel: string;
    bottleType?: string;
    clicks: number;
    conversions: number;
    date: string;
  }>;
}

const QRTrackingComponent: React.FC = () => {
  const [qrCodes, setQrCodes] = useState<QRCode[]>([]);
  const [analytics, setAnalytics] = useState<QRCodeAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  // Surfaced in the UI rather than only console.error'd. This panel showed
  // invented numbers before; a failed load must read as a failure, not as an
  // empty but healthy account.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showQRViewer, setShowQRViewer] = useState(false);
  const [selectedQRCode, setSelectedQRCode] = useState<QRCode | null>(null);
  const [showBatchCreator, setShowBatchCreator] = useState(false);
  const [activeTab, setActiveTab] = useState<QRView>('overview');

  const [newQRCode, setNewQRCode] = useState({
    name: '',
    channel: '',
    description: '',
    url: '',
    bottleType: 'iced-espresso-martini',
    batchNumber: '',
    campaignId: '',
    campaignName: '',
    costPerClick: 0,
    totalSpent: 0,
  });

  const [batchData, setBatchData] = useState({
    bottleType: 'iced-espresso-martini',
    batchNumber: '',
    quantity: 100,
    baseUrl: 'https://waddleandsip.com/join',
    campaignId: '',
    campaignName: '',
    productionDate: '',
    expiryDate: '',
  });

  useEffect(() => {
    fetchQRCodes();
    fetchAnalytics();
  }, []);

  const fetchQRCodes = async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const response = await apiService.getQRCodes();
      if (response.success) {
        setQrCodes((response.qrCodes as QRCode[]) || []);
      } else {
        setLoadError(response.message || 'Could not load QR codes');
      }
    } catch (error) {
      console.error('Error fetching QR codes:', error);
      setLoadError('Could not reach the server to load QR codes');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await apiService.getQRAnalytics();
      if (response.success) {
        setAnalytics((response.analytics as QRCodeAnalytics) || null);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const createQRCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoadError(null);
    try {
      const response = await apiService.createQRCode(newQRCode);
      if (!response.success) {
        setLoadError(response.message || 'Could not create the QR code');
        return;
      }

      // Re-read rather than pushing the local object into state: the server
      // assigns the code and renders the QR image, so the saved record is the
      // only one that can actually be scanned.
      await Promise.all([fetchQRCodes(), fetchAnalytics()]);
      setNewQRCode({ name: '', channel: '', description: '', url: '', bottleType: 'iced-espresso-martini', batchNumber: '', campaignId: '', campaignName: '', costPerClick: 0, totalSpent: 0 });
    } catch (error) {
      console.error('Error creating QR code:', error);
      setLoadError('Could not reach the server to create the QR code');
    }
  };

  const createBottleBatch = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoadError(null);
    try {
      const response = await apiService.createBottleBatch(batchData);
      if (!response.success) {
        setLoadError(response.message || 'Could not create the batch');
        return;
      }

      await Promise.all([fetchQRCodes(), fetchAnalytics()]);
      setShowBatchCreator(false);
      setBatchData({ bottleType: 'iced-espresso-martini', batchNumber: '', quantity: 100, baseUrl: 'https://waddleandsip.com/join', campaignId: '', campaignName: '', productionDate: '', expiryDate: '' });
    } catch (error) {
      console.error('Error creating bottle batch:', error);
      setLoadError('Could not reach the server to create the batch');
    }
  };

  const toggleQRCodeStatus = async (qrCodeId: string) => {
    try {
      setQrCodes(prev => prev.map(qr =>
        qr._id === qrCodeId ? { ...qr, isActive: !qr.isActive } : qr
      ));
    } catch (error) {
      console.error('Error toggling QR code status:', error);
    }
  };

  const deleteQRCode = async (qrCodeId: string) => {
    try {
      setQrCodes(prev => prev.filter(qr => qr._id !== qrCodeId));
    } catch (error) {
      console.error('Error deleting QR code:', error);
    }
  };

  const viewQRCode = (qrCode: QRCode) => {
    setSelectedQRCode(qrCode);
    setShowQRViewer(true);
  };

  const getConversionRate = (conversions: number, clicks: number) => {
    return clicks > 0 ? ((conversions / clicks) * 100).toFixed(2) : '0.00';
  };

  const getROI = (conversions: number, totalSpent: number) => {
    const revenue = conversions * 10;
    return totalSpent > 0 ? (((revenue - totalSpent) / totalSpent) * 100).toFixed(2) : '0.00';
  };

  const viewRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const handleViewKeyDown = (event: React.KeyboardEvent, index: number) => {
    const moves: Record<string, number> = { ArrowRight: 1, ArrowLeft: -1 };
    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      const target = event.key === 'Home' ? 0 : QR_VIEWS.length - 1;
      viewRefs.current[target]?.focus();
      setActiveTab(QR_VIEWS[target].key);
      return;
    }
    const move = moves[event.key];
    if (move === undefined) return;
    event.preventDefault();
    const next = (index + move + QR_VIEWS.length) % QR_VIEWS.length;
    viewRefs.current[next]?.focus();
    setActiveTab(QR_VIEWS[next].key);
  };

  return (
    <div className="space-y-6">
      {/* These are views *within* QR Tracking, one level below the section's
          own tabs. As a second underlined strip stacked directly beneath those
          they read as siblings competing for the same job, so they are a
          segmented control instead — a different shape for a different level.
          Wired as a tablist so the set, the selection, and arrow-key movement
          are all exposed rather than implied by an underline. */}
      <div
        role="tablist"
        aria-label="QR tracking views"
        className="inline-flex flex-wrap gap-1 rounded-lg bg-surface-sunken p-1"
      >
        {QR_VIEWS.map(({ key, label }, index) => {
          const isActive = activeTab === key;
          return (
            <button
              key={key}
              ref={(node) => {
                viewRefs.current[index] = node;
              }}
              type="button"
              role="tab"
              aria-selected={isActive}
              tabIndex={isActive ? 0 : -1}
              onClick={() => setActiveTab(key)}
              onKeyDown={(event) => handleViewKeyDown(event, index)}
              className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue ${
                isActive
                  ? 'bg-surface text-brand-blue shadow-card'
                  : 'text-ink-2 hover:text-ink'
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* A failed load has to look different from a quiet account. This panel
          used to show invented figures, so "nothing here" and "we could not
          ask" must not render the same way. */}
      {loadError && (
        <div className="bg-brand-red/10 border border-brand-red/30 rounded-xl p-4">
          <p className="text-brand-red font-body">{loadError}</p>
        </div>
      )}

      {isLoading && (
        <div className="bg-ice-pale border border-ice-teal-light rounded-xl p-4 text-center">
          <p className="text-deep-navy/70 font-body">Loading QR data…</p>
        </div>
      )}

      {!isLoading && !loadError && qrCodes.length === 0 && (
        <div className="bg-ice-pale border border-ice-teal-light rounded-xl p-6 text-center">
          <p className="text-deep-navy font-body font-bold mb-1">No QR codes yet</p>
          <p className="text-deep-navy/70 font-body text-sm">
            Scans are only counted for codes created here. Create one below, then print or
            share the tracking URL it gives you — that URL is what records the scan.
          </p>
        </div>
      )}

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          {/* Create a single QR code. The handler and its form state existed
              already; only this form was missing, so nothing could be created
              outside a bottle batch. */}
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Create a QR Code</h3>
            <form onSubmit={createQRCode} className="grid md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="qr-new-name" className="block text-deep-navy font-bold mb-2">Name</label>
                <input
                  id="qr-new-name"
                  type="text"
                  required
                  value={newQRCode.name}
                  onChange={(e) => setNewQRCode({ ...newQRCode, name: e.target.value })}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-800 focus:outline-none focus:border-brand-blue"
                  placeholder="e.g., Instagram bio link"
                />
              </div>
              <div>
                <label htmlFor="qr-new-channel" className="block text-deep-navy font-bold mb-2">Channel</label>
                <select
                  id="qr-new-channel"
                  required
                  value={newQRCode.channel}
                  onChange={(e) => setNewQRCode({ ...newQRCode, channel: e.target.value })}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-800 focus:outline-none focus:border-brand-blue"
                >
                  <option value="">Select a channel</option>
                  {['instagram', 'tiktok', 'twitter', 'facebook', 'youtube', 'linkedin', 'print', 'events', 'influencer', 'bottle', 'packaging', 'retail', 'other'].map((channel) => (
                    <option key={channel} value={channel}>{channel}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label htmlFor="qr-new-url" className="block text-deep-navy font-bold mb-2">Destination URL</label>
                <input
                  id="qr-new-url"
                  type="url"
                  required
                  value={newQRCode.url}
                  onChange={(e) => setNewQRCode({ ...newQRCode, url: e.target.value })}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-800 focus:outline-none focus:border-brand-blue"
                  placeholder="https://www.waddleandsip.com/preorder"
                />
                <p className="text-gray-500 text-sm mt-1">
                  Where the scan lands. The QR itself encodes a tracking URL that counts the
                  scan and then forwards here.
                </p>
              </div>
              <div className="md:col-span-2">
                <button
                  type="submit"
                  className="bg-brand-blue hover:bg-brand-blue-dark text-white font-bold px-6 py-3 rounded-xl transition-colors"
                >
                  Create QR Code
                </button>
              </div>
            </form>
          </div>

          {/* QR Code Stats */}
          {analytics && (
            <div className="grid md:grid-cols-4 gap-6">
              <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
                <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Total QR Codes</h3>
                <div className="text-3xl font-bold text-brand-blue">{analytics.totalCodes}</div>
                <p className="text-gray-500 text-sm">Active: {analytics.activeCodes}</p>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
                <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Total Clicks</h3>
                <div className="text-3xl font-bold text-brand-blue-light">{analytics.totalClicks.toLocaleString()}</div>
                <p className="text-gray-500 text-sm">All time</p>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
                <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Conversions</h3>
                <div className="text-3xl font-bold text-scarf-green">{analytics.totalConversions}</div>
                <p className="text-gray-500 text-sm">{analytics.overallConversionRate}% rate</p>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
                <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Total Spent</h3>
                <div className="text-3xl font-bold text-brand-red">${analytics.totalSpent.toFixed(2)}</div>
                <p className="text-gray-500 text-sm">Marketing budget</p>
              </div>
            </div>
          )}

          {/* Channel Performance */}
          {analytics && (
            <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
              <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Channel Performance</h3>
              <div className="grid md:grid-cols-3 gap-4">
                {analytics.channelPerformance.map((channel) => (
                  <div key={channel._id} className="bg-frost-white rounded-xl p-4">
                    <h4 className="font-bold text-deep-navy mb-2">{channel._id.toUpperCase()}</h4>
                    <div className="text-2xl font-bold text-brand-blue">{channel.totalClicks.toLocaleString()}</div>
                    <div className="text-sm text-gray-500">
                      {channel.totalConversions} conversions ({channel.avgConversionRate.toFixed(2)}%)
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Activity */}
          {analytics && (
            <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
              <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Recent Activity</h3>
              <div className="space-y-3">
                {analytics.recentActivity.map((activity, index) => (
                  <div key={index} className="flex justify-between items-center bg-frost-white rounded-xl p-3">
                    <div>
                      <div className="font-bold text-deep-navy">{activity.code}</div>
                      <div className="text-sm text-gray-500">{activity.channel} {activity.bottleType ? `• ${activity.bottleType}` : ''}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-brand-blue">{activity.clicks} clicks</div>
                      <div className="text-sm text-gray-500">{activity.conversions} conversions</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Bottles Tab */}
      {activeTab === 'bottles' && (
        <>
          {/* Batch Creator */}
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-title font-bold text-deep-navy">Bottle QR Code Management</h3>
              <button
                onClick={() => setShowBatchCreator(!showBatchCreator)}
                className="bg-brand-blue hover:bg-brand-blue-dark text-white font-bold px-4 py-2 rounded-xl transition-colors"
              >
                {showBatchCreator ? 'Cancel' : 'Create Bottle Batch'}
              </button>
            </div>

            {showBatchCreator && (
              <form onSubmit={createBottleBatch} className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="qr-bottle-type" className="block text-deep-navy font-bold mb-2">Bottle Type</label>
                  <select
                    id="qr-bottle-type"
                    value={batchData.bottleType}
                    onChange={(e) => setBatchData({ ...batchData, bottleType: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-800 focus:outline-none focus:border-brand-blue transition-colors"
                  >
                    <option value="iced-espresso-martini">Iced Espresso Martini</option>
                    <option value="classic-martini">Classic Martini</option>
                    <option value="cosmopolitan">Cosmopolitan</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="qr-batch-number" className="block text-deep-navy font-bold mb-2">Batch Number</label>
                  <input
                    id="qr-batch-number"
                    type="text"
                    value={batchData.batchNumber}
                    onChange={(e) => setBatchData({ ...batchData, batchNumber: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:border-brand-blue transition-colors"
                    placeholder="e.g., A001, B002"
                  />
                </div>
                <div>
                  <label htmlFor="qr-quantity" className="block text-deep-navy font-bold mb-2">Quantity</label>
                  <input
                    id="qr-quantity"
                    type="number"
                    value={batchData.quantity}
                    onChange={(e) => setBatchData({ ...batchData, quantity: Number.parseInt(e.target.value, 10) })}
                    required
                    min="1"
                    max="10000"
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:border-brand-blue transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="qr-base-url" className="block text-deep-navy font-bold mb-2">Base URL</label>
                  <input
                    id="qr-base-url"
                    type="url"
                    value={batchData.baseUrl}
                    onChange={(e) => setBatchData({ ...batchData, baseUrl: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:border-brand-blue transition-colors"
                    placeholder="https://waddleandsip.com/join"
                  />
                </div>
                <div>
                  <label htmlFor="qr-campaign-id" className="block text-deep-navy font-bold mb-2">Campaign ID</label>
                  <input
                    id="qr-campaign-id"
                    type="text"
                    value={batchData.campaignId}
                    onChange={(e) => setBatchData({ ...batchData, campaignId: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:border-brand-blue transition-colors"
                    placeholder="Optional campaign ID"
                  />
                </div>
                <div>
                  <label htmlFor="qr-campaign-name" className="block text-deep-navy font-bold mb-2">Campaign Name</label>
                  <input
                    id="qr-campaign-name"
                    type="text"
                    value={batchData.campaignName}
                    onChange={(e) => setBatchData({ ...batchData, campaignName: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:border-brand-blue transition-colors"
                    placeholder="Optional campaign name"
                  />
                </div>
                <div>
                  <label htmlFor="qr-production-date" className="block text-deep-navy font-bold mb-2">Production Date</label>
                  <input
                    id="qr-production-date"
                    type="date"
                    value={batchData.productionDate}
                    onChange={(e) => setBatchData({ ...batchData, productionDate: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-800 focus:outline-none focus:border-brand-blue transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="qr-expiry-date" className="block text-deep-navy font-bold mb-2">Expiry Date</label>
                  <input
                    id="qr-expiry-date"
                    type="date"
                    value={batchData.expiryDate}
                    onChange={(e) => setBatchData({ ...batchData, expiryDate: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-800 focus:outline-none focus:border-brand-blue transition-colors"
                  />
                </div>
                <div className="md:col-span-2">
                  <button
                    type="submit"
                    className="w-full bg-brand-blue hover:bg-brand-blue-dark text-white font-bold px-6 py-3 rounded-xl transition-colors"
                  >
                    Create {batchData.quantity} Bottle QR Codes
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Bottle QR Codes Table */}
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Bottle QR Codes</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-ice-teal-light">
                    <th className="text-left py-3 px-4 text-deep-navy font-bold">Name</th>
                    <th className="text-left py-3 px-4 text-deep-navy font-bold">Batch</th>
                    <th className="text-left py-3 px-4 text-deep-navy font-bold">Code</th>
                    <th className="text-left py-3 px-4 text-deep-navy font-bold">Clicks</th>
                    <th className="text-left py-3 px-4 text-deep-navy font-bold">Conversions</th>
                    <th className="text-left py-3 px-4 text-deep-navy font-bold">Rate</th>
                    <th className="text-left py-3 px-4 text-deep-navy font-bold">Status</th>
                    <th className="text-left py-3 px-4 text-deep-navy font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {qrCodes.filter(qr => qr.channel === 'bottle').map((qrCode) => (
                    <tr key={qrCode._id} className="border-b border-gray-100 hover:bg-frost-white transition-colors">
                      <td className="py-3 px-4">
                        <div>
                          <div className="font-bold text-deep-navy">{qrCode.name}</div>
                          <div className="text-gray-500 text-sm">{qrCode.description}</div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-1 bg-ice-teal/30 text-brand-blue rounded text-xs font-bold">
                          {qrCode.batchNumber || 'N/A'}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-mono text-sm text-gray-700">{qrCode.code}</td>
                      <td className="py-3 px-4 text-gray-800">{qrCode.totalClicks.toLocaleString()}</td>
                      <td className="py-3 px-4 text-gray-800">{qrCode.conversions}</td>
                      <td className="py-3 px-4">
                        <span className={`font-bold ${
                          Number.parseFloat(getConversionRate(qrCode.conversions, qrCode.totalClicks)) >= 5
                            ? 'text-scarf-green' : 'text-brand-red'
                        }`}>
                          {getConversionRate(qrCode.conversions, qrCode.totalClicks)}%
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded text-xs font-bold ${
                          qrCode.isActive ? 'bg-ice-teal/30 text-brand-blue' : 'bg-red-100 text-brand-red'
                        }`}>
                          {qrCode.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => viewQRCode(qrCode)}
                            className="px-2 py-1 bg-brand-blue-light hover:bg-brand-blue text-white rounded text-xs font-bold transition-colors"
                          >
                            View
                          </button>
                          <button
                            onClick={() => toggleQRCodeStatus(qrCode._id)}
                            className={`px-2 py-1 rounded text-xs font-bold transition-colors ${
                              qrCode.isActive
                                ? 'bg-brand-red hover:bg-brand-red-light text-white'
                                : 'bg-brand-blue hover:bg-brand-blue-dark text-white'
                            }`}
                          >
                            {qrCode.isActive ? 'Pause' : 'Activate'}
                          </button>
                          <button
                            onClick={() => deleteQRCode(qrCode._id)}
                            className="px-2 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-xs font-bold transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && analytics && (
        <>
          {/* Geographic Performance */}
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Geographic Performance</h3>
            <div className="grid md:grid-cols-3 gap-4">
              {analytics.geographicPerformance.map((geo) => (
                <div key={geo._id} className="bg-frost-white rounded-xl p-4">
                  <h4 className="font-bold text-deep-navy mb-2">{geo._id}</h4>
                  <div className="text-2xl font-bold text-brand-blue">{geo.totalClicks.toLocaleString()}</div>
                  <div className="text-sm text-gray-500">
                    {geo.totalConversions} conversions
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Device Performance */}
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Device Performance</h3>
            <div className="grid md:grid-cols-3 gap-4">
              {analytics.devicePerformance.map((device) => (
                <div key={device._id} className="bg-frost-white rounded-xl p-4">
                  <h4 className="font-bold text-deep-navy mb-2">{device._id.toUpperCase()}</h4>
                  <div className="text-2xl font-bold text-brand-blue-light">{device.totalClicks.toLocaleString()}</div>
                  <div className="text-sm text-gray-500">
                    {device.totalConversions} conversions
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bottle Performance */}
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Bottle Type Performance</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {analytics.bottlePerformance.map((bottle) => (
                <div key={bottle._id} className="bg-frost-white rounded-xl p-4">
                  <h4 className="font-bold text-deep-navy mb-2">{bottle._id.replace('-', ' ').toUpperCase()}</h4>
                  <div className="text-2xl font-bold text-brand-blue">{bottle.totalClicks.toLocaleString()}</div>
                  <div className="text-sm text-gray-500">
                    {bottle.totalConversions} conversions ({bottle.avgConversionRate.toFixed(2)}%)
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ROI Tab */}
      {activeTab === 'roi' && (
        <>
          {/* ROI Overview */}
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-xl font-title font-bold text-deep-navy mb-4">ROI Analysis</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-frost-white rounded-xl p-4">
                <h4 className="font-bold text-deep-navy mb-2">Total Revenue</h4>
                <div className="text-2xl font-bold text-scarf-green">
                  ${(qrCodes.reduce((sum, qr) => sum + (qr.conversions * 10), 0)).toFixed(2)}
                </div>
                <div className="text-sm text-gray-500">Based on $10 avg order</div>
              </div>
              <div className="bg-frost-white rounded-xl p-4">
                <h4 className="font-bold text-deep-navy mb-2">Total Spent</h4>
                <div className="text-2xl font-bold text-brand-red">
                  ${qrCodes.reduce((sum, qr) => sum + (qr.totalSpent || 0), 0).toFixed(2)}
                </div>
                <div className="text-sm text-gray-500">Marketing budget</div>
              </div>
              <div className="bg-frost-white rounded-xl p-4">
                <h4 className="font-bold text-deep-navy mb-2">Overall ROI</h4>
                <div className="text-2xl font-bold text-brand-blue">
                  {getROI(
                    qrCodes.reduce((sum, qr) => sum + qr.conversions, 0),
                    qrCodes.reduce((sum, qr) => sum + (qr.totalSpent || 0), 0)
                  )}%
                </div>
                <div className="text-sm text-gray-500">Return on investment</div>
              </div>
            </div>
          </div>

          {/* ROI by Campaign */}
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-xl font-title font-bold text-deep-navy mb-4">ROI by Campaign</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-ice-teal-light">
                    <th className="text-left py-3 px-4 text-deep-navy font-bold">Campaign</th>
                    <th className="text-left py-3 px-4 text-deep-navy font-bold">Clicks</th>
                    <th className="text-left py-3 px-4 text-deep-navy font-bold">Conversions</th>
                    <th className="text-left py-3 px-4 text-deep-navy font-bold">Spent</th>
                    <th className="text-left py-3 px-4 text-deep-navy font-bold">Revenue</th>
                    <th className="text-left py-3 px-4 text-deep-navy font-bold">ROI</th>
                  </tr>
                </thead>
                <tbody>
                  {qrCodes.filter(qr => qr.campaignId).map((qrCode) => {
                    const revenue = qrCode.conversions * 10;
                    const roi = qrCode.totalSpent && qrCode.totalSpent > 0 ?
                      (((revenue - qrCode.totalSpent) / qrCode.totalSpent) * 100).toFixed(2) : '0.00';

                    return (
                      <tr key={qrCode._id} className="border-b border-gray-100 hover:bg-frost-white transition-colors">
                        <td className="py-3 px-4">
                          <div>
                            <div className="font-bold text-deep-navy">{qrCode.campaignName || qrCode.campaignId}</div>
                            <div className="text-gray-500 text-sm">{qrCode.channel}</div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-gray-800">{qrCode.totalClicks.toLocaleString()}</td>
                        <td className="py-3 px-4 text-gray-800">{qrCode.conversions}</td>
                        <td className="py-3 px-4 text-gray-800">${(qrCode.totalSpent || 0).toFixed(2)}</td>
                        <td className="py-3 px-4 text-gray-800">${revenue.toFixed(2)}</td>
                        <td className="py-3 px-4">
                          <span className={`font-bold ${
                            Number.parseFloat(roi) >= 0 ? 'text-scarf-green' : 'text-brand-red'
                          }`}>
                            {roi}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* QR Code Viewer Modal */}
      {showQRViewer && selectedQRCode && (
        <QRCodeViewer
          qrCodeUrl={selectedQRCode.qrCodeUrl}
          code={selectedQRCode.code}
          name={selectedQRCode.name}
          channel={selectedQRCode.channel}
          url={selectedQRCode.url}
          onClose={() => {
            setShowQRViewer(false);
            setSelectedQRCode(null);
          }}
        />
      )}
    </div>
  );
};

export default QRTrackingComponent;
